/**
 * useFormUndoKeybindings — the undo/redo accelerator every surface that edits a
 * document mounts: `FormPageContent` for a form version, `RecordDetailPanel` for an
 * entity record. It resolves chords through the keyboard schema (`form.undo` /
 * `form.redo`) and hands them to `useFormUndoDispatch`, the same path
 * `FormUndoButtons` runs.
 *
 * It owns the stack's lifetime, and the document being edited is what bounds it: the
 * stack is cleared on both edges of this effect, and the effect re-runs whenever
 * `editedDocumentPath` changes. Nothing recorded against one document survives into
 * another, so a button naming a delete can never be showing it over a record that
 * delete never touched — the record browser swaps records in place, without
 * unmounting the surface.
 *
 * It deliberately skips `useGlobalKeybindings`'s `isEditableTarget` guard — the user
 * IS typing in a cell when the chord arrives — and scopes by origin instead. The
 * deep `formUndoStack` import keeps `formStore` and firebase out of the spec.
 */

import { useEffect, type RefObject } from 'react';
import { keybindingRegistry, matchChord } from '@/keyboard';
import { formUndoStack } from '@/store/form/formUndoStack';
import { useFormUndoDispatch } from './useFormUndoDispatch';

/**
 * Marks a drawer whose editable content registers no undo target, so the chord must
 * be left to the browser. `CompositeFieldSidebar` is the case: `useFieldUI` nulls
 * `undoTargetKey` under a `CompositeFieldContext`, because a sub-field writes
 * through its parent's writer. Without the marker, ⌘Z there would revert an
 * unrelated main-form edit the user cannot see.
 */
export const NATIVE_UNDO_DRAWER_CLASS = 'native-undo-drawer';

/**
 * Transient antd overlays, excluded even when one renders inside the form body
 * (`getPopupContainer`, `getContainer={false}`); a portalled one is already
 * outside the form root and excluded by containment alone.
 */
const OVERLAY_SELECTOR = [
    '.ant-modal-root',
    '.ant-popover',
    '.ant-dropdown',
    '.ant-select-dropdown',
    '.ant-picker-dropdown',
    '.ant-tooltip',
    '.ant-image-preview-root',
].join(',');

/**
 * An unmarked drawer owns the chord: `FormSidebar` hosts entity-record fields, which
 * go through `useFieldUI` on a real document path and do register undo targets.
 */
const UNDO_SURFACE_SELECTOR = `.ant-drawer:not(.${NATIVE_UNDO_DRAWER_CLASS})`;

/** Every antd container that can claim the origin; the nearest one decides. */
const CONTAINER_SELECTOR = `${OVERLAY_SELECTOR},.ant-drawer`;

/** Non-antd dialogs, consulted only once no antd container has claimed the origin. */
const BARE_DIALOG_SELECTOR = '[role="dialog"],[role="alertdialog"]';

const isEditable = (element: Element): boolean => {
    const tag = element.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return element instanceof HTMLElement && element.isContentEditable;
};

/**
 * True when the chord belongs to the undo stack: it came from a surface whose
 * fields and cells register undo targets — the form body or an unmarked drawer — or
 * from somewhere with no native undo to steal. A transient overlay never owns it,
 * so a modal's text field and a portalled dropdown keep the browser's
 * per-character undo, as does any editable outside the form entirely.
 *
 * The nearest enclosing antd container decides, so a modal opened from within a
 * drawer is still excluded, and so is a marked drawer opened from within a plain
 * one. `role="dialog"` is consulted only afterwards, because a drawer's own panel
 * carries it and would otherwise disown the drawer.
 */
const ownsChord = (event: KeyboardEvent, formRoot: HTMLElement | null): boolean => {
    const origin = event.target instanceof Element ? event.target : document.activeElement;
    if (origin === null || origin === document.body || origin === document.documentElement) return true;

    const container = origin.closest(CONTAINER_SELECTOR);
    if (container !== null) return container.matches(UNDO_SURFACE_SELECTOR);
    if (origin.closest(BARE_DIALOG_SELECTOR) !== null) return false;

    if (formRoot !== null && formRoot.contains(origin)) return true;
    return !isEditable(origin);
};

/**
 * @param formRootRef the form body element whose fields and cells own the chord.
 * @param editedDocumentPath the document this surface is editing — a form version
 * path or an entity record path, `undefined` while none is open. It carries no
 * behaviour inside the effect; it is the effect's identity, and that is the point.
 */
export const useFormUndoKeybindings = (
    formRootRef: RefObject<HTMLElement | null>,
    editedDocumentPath: string | undefined,
): void => {
    const dispatch = useFormUndoDispatch();

    useEffect(() => {
        /**
         * The stack is module state but only this listener can pop it, so its life is
         * this effect's. Clearing on entry drops whatever was recorded while nothing
         * was listening; clearing on exit stops one document's entries from outliving
         * the surface that could show them being reverted, whether the surface
         * unmounted or simply moved to another document.
         */
        formUndoStack.clear();

        const undoChords = keybindingRegistry.byId('form.undo')?.chords ?? [];
        const redoChords = keybindingRegistry.byId('form.redo')?.chords ?? [];

        const onKeyDown = (e: KeyboardEvent): void => {
            const isRedo = redoChords.some((chord) => matchChord(e, chord));
            const isUndo = !isRedo && undoChords.some((chord) => matchChord(e, chord));
            if (!isRedo && !isUndo) return;

            /**
             * The same gate the buttons render, read at event time off the bare proxy:
             * the stack holds an entry AND no step is running. Gating on stack length
             * instead would let a held chord start a second step while the first is
             * still in flight, reversing two operations for one press — the race the
             * buttons are disabled to prevent.
             *
             * The two reasons it refuses want opposite endings. A step in flight
             * consumes the chord: a held one repeats, and the second press reaching a
             * focused cell would run the input's own native undo over text this stack
             * never recorded — a revert nothing on screen accounts for. An empty stack
             * lets it through, because per-character undo is then the only undo the
             * surface has.
             */
            if (!formUndoStack.availability[isRedo ? 'redo' : 'undo'].available) {
                if (formUndoStack.stepping() && ownsChord(e, formRootRef.current)) {
                    e.preventDefault();
                }
                return;
            }
            if (!ownsChord(e, formRootRef.current)) return;

            e.preventDefault();
            dispatch(isRedo ? 'redo' : 'undo');
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            formUndoStack.clear();
        };
    }, [formRootRef, dispatch, editedDocumentPath]);
};
